import ExcelJS from 'exceljs'
import { createFieldFromLibrary } from '@/components/form-builder/field-config'
import { extractFormulaRefs, normalizeFormulaOperatorSymbols } from '@/lib/formula'
import { normalizeCrossFormRefSyntax, parseCrossFormRef, remapFormulaTokens } from '@/lib/crossForm'
import type { FormField, FormSection, FormMeta } from '@/components/form-builder/types'
import {
  FORM_CATEGORY_LABELS,
  FORM_CATEGORY_OPTIONS,
  supportsAttachment,
  type AttachmentConfig,
  type FieldDefinition,
  type FormCategory,
  type FormFieldLayout,
  type LabelPosition,
  type LabelWidth,
  type SectionWidth,
} from '@/types/cgt'
import {
  LABEL_POSITIONS,
  LABEL_POSITION_LABELS,
  LABEL_WIDTHS,
  LABEL_WIDTH_LABELS,
} from '@/lib/fieldLayout'
import type { FieldCategory } from '@/api/fieldCategoriesApi'

// ─── Excel <-> form-builder bridge ───────────────────────────────────────────
//
// The form builder saves `{ name, category, sections[] }`, and every field is a
// COPY of a Field Library entry (see createFieldFromLibrary). So the spreadsheet
// only has to say WHICH library field goes in WHICH section — it never re-encodes
// field config. This module generates that spreadsheet (with real dropdowns
// sourced from the library) and parses a filled one back into builder state for
// review before save. It performs NO network or React work — callers pass the
// library fields in and get plain data out.

export const SHEET_FORM = 'Form Creator'
export const SHEET_LIBRARY = 'Library_Field'
/** Placeholder left in empty field-slot cells; skipped on import. */
export const SELECTION_PLACEHOLDER = '[-- Selection --]'
/** Empty dropdown-enabled field rows seeded under each section in the template. */
const FIELD_SLOTS_PER_SECTION = 8
/** Example section blocks seeded in a fresh template. */
const TEMPLATE_SECTIONS = 2

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const FORM_TEMPLATE_MIME = XLSX_MIME

// Row keys (column A) recognised in the Form Creator sheet, matched case-insensitively.
const KEY_FORM_NAME = 'form name'
const KEY_FORM_TYPE = 'form type'
const KEY_SECTION = 'section'
const KEY_HEADER = 'header'
const KEY_SECTION_WIDTH = 'section width'
// Form-wide label default (z8nrz7djyx). Blank = no default; fields keep the
// system layout.
const KEY_LABEL_POSITION = 'label position'
const KEY_LABEL_WIDTH = 'label width'
// Accept the historical "Filed Name" typo as an alias so older templates still import.
const FIELD_NAME_KEYS = new Set(['field name', 'filed name'])
/**
 * A field row whose column A reads "Text Block" places a text block
 * (z8nrz7djyy) with column B as its text, rather than naming a library field.
 * A text block is authored on the form, not drawn from the library, so it is
 * the one field the sheet spells out in full. Line breaks typed in the cell
 * (Alt+Enter) are kept.
 */
const KEY_TEXT_BLOCK = 'text block'
const TEXT_BLOCK_HINT =
  'Pick a library field in column A — or write "Text Block" there and its text in column B.'

const WIDTH_LABELS: Record<SectionWidth, string> = {
  full: 'Full',
  half: 'Half',
  third: 'Third',
  quarter: 'Quarter',
}
const WIDTH_VALUES = Object.keys(WIDTH_LABELS) as SectionWidth[]

const LABEL_POSITION_BY_TEXT = new Map<string, LabelPosition>(
  LABEL_POSITIONS.map(p => [LABEL_POSITION_LABELS[p].toLowerCase(), p]),
)
const LABEL_WIDTH_BY_TEXT = new Map<string, LabelWidth>(
  LABEL_WIDTHS.map(w => [LABEL_WIDTH_LABELS[w].toLowerCase(), w]),
)

const CATEGORY_VALUES = FORM_CATEGORY_OPTIONS
// Lower-cased label -> category, for parsing the human-facing "Form Type" cell.
const CATEGORY_BY_LABEL = new Map<string, FormCategory>(
  CATEGORY_VALUES.map(c => [FORM_CATEGORY_LABELS[c].toLowerCase(), c]),
)

// ─── display names (label collision handling) ────────────────────────────────
//
// Users pick a field by NAME — never by id. The dropdown lists names and the
// parser resolves names. The only wrinkle: LibraryField.label is NOT unique, so
// two library fields can share a name. For that rare case (and ONLY that case)
// we make the display name unique with human-readable qualifiers — the field
// type, then a numeric suffix if type still doesn't disambiguate — so a raw uuid
// is never shown. Unique names stay completely clean. build + parse MUST compute
// these identically.

function computeDisplayNames(
  fields: FieldDefinition[],
): { displayById: Map<string, string>; byDisplay: Map<string, FieldDefinition> } {
  const groups = new Map<string, FieldDefinition[]>()
  for (const f of fields) {
    const g = groups.get(f.label) ?? []
    g.push(f)
    groups.set(f.label, g)
  }

  const displayById = new Map<string, string>()
  const byDisplay = new Map<string, FieldDefinition>()
  for (const [label, group] of groups) {
    if (group.length === 1) {
      displayById.set(group[0].id, label)
      byDisplay.set(label, group[0])
      continue
    }
    // Deterministic order so the same library always yields the same names.
    const sorted = [...group].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    const typedName = sorted.map(f => `${label} (${f.fieldType})`)
    const typeCounts = new Map<string, number>()
    for (const n of typedName) typeCounts.set(n, (typeCounts.get(n) ?? 0) + 1)
    const seen = new Map<string, number>()
    sorted.forEach((f, i) => {
      let display = typedName[i]
      if ((typeCounts.get(display) ?? 0) > 1) {
        const n = (seen.get(display) ?? 0) + 1
        seen.set(display, n)
        display = `${label} (${f.fieldType} ${n})`
      }
      displayById.set(f.id, display)
      byDisplay.set(display, f)
    })
  }
  return { displayById, byDisplay }
}

// ─── template generation ─────────────────────────────────────────────────────

/**
 * Build the .xlsx template: a `Library_Field` sheet listing every library field
 * (the dropdown source) and a `Form Creator` sheet with a form-level header plus
 * example section blocks whose field rows are real data-validation dropdowns.
 * Returns the workbook bytes for download.
 */
export async function buildFormTemplate(
  libraryFields: FieldDefinition[],
): Promise<ArrayBuffer> {
  const { displayById } = computeDisplayNames(libraryFields)
  const sorted = [...libraryFields].sort((a, b) =>
    displayById.get(a.id)!.localeCompare(displayById.get(b.id)!),
  )

  const wb = new ExcelJS.Workbook()

  // Library_Field sheet — the dropdown source (col A) plus read-only context so
  // users recognise a field by name/type, never by id.
  const lib = wb.addWorksheet(SHEET_LIBRARY)
  lib.addRow(['Field Name', 'Type', 'Required'])
  lib.getRow(1).font = { bold: true }
  for (const f of sorted) {
    lib.addRow([displayById.get(f.id)!, f.fieldType, f.required ? 'Yes' : 'No'])
  }
  lib.getColumn(1).width = 40
  lib.getColumn(2).width = 14
  lib.getColumn(3).width = 12
  // Range the Form Creator dropdowns pull from (skip the header row).
  const lastLibRow = Math.max(2, sorted.length + 1)
  const fieldListFormula = `=${SHEET_LIBRARY}!$A$2:$A$${lastLibRow}`

  // Form Creator sheet.
  const ws = wb.addWorksheet(SHEET_FORM)
  ws.getColumn(1).width = 26
  ws.getColumn(2).width = 44

  const setKey = (row: number, key: string, value: string | number = '') => {
    ws.getCell(`A${row}`).value = key
    ws.getCell(`A${row}`).font = { bold: true }
    if (value !== '') ws.getCell(`B${row}`).value = value
  }

  let r = 1
  setKey(r++, 'Form Name')
  setKey(r++, 'Form Type', FORM_CATEGORY_LABELS.clinical)
  ws.getCell(`B${r - 1}`).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [`"${CATEGORY_VALUES.map(c => FORM_CATEGORY_LABELS[c]).join(',')}"`],
  }
  setKey(r++, 'Label Position')
  ws.getCell(`B${r - 1}`).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [`"${LABEL_POSITIONS.map(p => LABEL_POSITION_LABELS[p]).join(',')}"`],
  }
  setKey(r++, 'Label Width')
  ws.getCell(`B${r - 1}`).dataValidation = {
    type: 'list',
    allowBlank: true,
    formulae: [`"${LABEL_WIDTHS.map(w => LABEL_WIDTH_LABELS[w]).join(',')}"`],
  }
  r++ // blank spacer

  for (let s = 1; s <= TEMPLATE_SECTIONS; s++) {
    setKey(r++, 'Section', s)
    setKey(r++, 'Header')
    setKey(r++, 'Section Width', WIDTH_LABELS.full)
    ws.getCell(`B${r - 1}`).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`"${WIDTH_VALUES.map(w => WIDTH_LABELS[w]).join(',')}"`],
    }
    setKey(r++, 'Field Name')
    ws.getCell(`A${r - 1}`).font = { bold: true }
    ws.getCell(`C${r - 1}`).value = TEXT_BLOCK_HINT
    ws.getCell(`C${r - 1}`).font = { italic: true, color: { argb: 'FF757575' } }
    for (let i = 0; i < FIELD_SLOTS_PER_SECTION; i++) {
      const cell = ws.getCell(`A${r++}`)
      cell.value = SELECTION_PLACEHOLDER
      cell.dataValidation = { type: 'list', allowBlank: true, formulae: [fieldListFormula] }
    }
    r++ // blank spacer between blocks
  }

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>
}

// ─── parsing ─────────────────────────────────────────────────────────────────

export interface ParsedForm {
  meta: FormMeta
  category: FormCategory
  sections: FormSection[]
  /** The sheet's Label Position / Label Width rows; absent when both are blank. */
  fieldLayout?: FormFieldLayout
}

export interface ParseFormResult {
  ok: boolean
  form?: ParsedForm
  /** Human-readable, row-referenced problems. */
  errors: string[]
  /**
   * Field names the sheet asks for that the Field Library does not have, deduped
   * and in first-appearance order, spelled exactly as the sheet spells them.
   * Kept out of `errors` because the remedy is one instruction for the whole set
   * ("create these in the Field Library first"), not a problem to fix per row.
   * `ok` is false whenever this is non-empty.
   */
  missingFieldNames: string[]
  /**
   * Formula operands that resolved to no field on the imported form — tokens
   * naming a library field the sheet never placed, or written before the field
   * carried a `refName` at all.
   *
   * NON-BLOCKING: `ok` can still be true. These fall through to the
   * label-similarity guess the builder has always applied, which is what the
   * "Re-map & Insert" dialog exists for, so rejecting the file here would
   * refuse imports that succeed today. Surfaced so the caller can say which
   * operands still need a look.
   */
  unresolvedRefs: string[]
}

/** Truthy spellings accepted in a Yes/No column, matching the existing
 *  Required/Enabled parsing. Input is expected already lowercased + trimmed. */
function isYes(raw: string): boolean {
  return raw === 'yes' || raw === 'true' || raw === '1'
}

/** Normalise any ExcelJS cell value to trimmed text. */
function cellText(value: ExcelJS.CellValue): string {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim()
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object') {
    const v = value as { text?: string; result?: unknown; richText?: { text: string }[] }
    if (Array.isArray(v.richText)) return v.richText.map(rt => rt.text).join('').trim()
    if (typeof v.text === 'string') return v.text.trim()
    if (v.result != null) return String(v.result).trim()
  }
  return ''
}

function parseWidth(text: string): SectionWidth | null {
  const t = text.trim().toLowerCase()
  if (!t) return 'full'
  return (WIDTH_VALUES as string[]).includes(t) ? (t as SectionWidth) : null
}

function parseCategory(text: string): FormCategory | null {
  const t = text.trim()
  if (!t) return 'clinical'
  if ((CATEGORY_VALUES as string[]).includes(t)) return t as FormCategory
  return CATEGORY_BY_LABEL.get(t.toLowerCase()) ?? null
}

interface PendingSection {
  startRow: number
  title: string
  width: SectionWidth
  fields: FieldDefinition[]
  /** Field rows in this block naming a field the library does not have. */
  unresolved: number
}

/**
 * Parse a filled template into builder state. All-or-nothing: any unresolved or
 * malformed row makes the whole import fail with no partial form. Failures come
 * back in two shapes — `missingFieldNames` for names the library does not have
 * (one instruction covers the whole set: create them first), and `errors` for
 * everything else, row-referenced. On success, fields are copied from the library
 * exactly as the palette does (createFieldFromLibrary), preserving `libraryFieldId`.
 */
export async function parseFormWorkbook(
  data: ArrayBuffer | Uint8Array,
  libraryFields: FieldDefinition[],
): Promise<ParseFormResult> {
  const { byDisplay } = computeDisplayNames(libraryFields)
  const byLabel = new Map<string, FieldDefinition[]>()
  for (const f of libraryFields) {
    const list = byLabel.get(f.label) ?? []
    list.push(f)
    byLabel.set(f.label, list)
  }

  const wb = new ExcelJS.Workbook()
  try {
    await wb.xlsx.load(data as ArrayBuffer)
  } catch {
    return {
      ok: false,
      errors: ['Could not read the file as an Excel workbook (.xlsx).'],
      missingFieldNames: [],
      unresolvedRefs: [],
    }
  }

  const ws = wb.getWorksheet(SHEET_FORM)
  if (!ws) {
    return {
      ok: false,
      errors: [`Missing a "${SHEET_FORM}" sheet — use the downloaded template.`],
      missingFieldNames: [],
      unresolvedRefs: [],
    }
  }

  const errors: string[] = []
  // Deduped, in first-appearance order — one field named in three sections is one
  // thing to create, not three.
  const missingFieldNames: string[] = []
  const missingSeen = new Set<string>()
  let formName = ''
  let formTypeText = ''
  const fieldLayout: FormFieldLayout = {}
  const sections: PendingSection[] = []
  let cur: PendingSection | null = null
  let inFields = false
  // Text blocks the sheet wrote out in full — placed without a library link.
  const sheetTextBlocks = new WeakSet<FieldDefinition>()

  // Resolve a field-slot cell to a library field. A name the library does not
  // have is collected for the missing-fields message; anything else that goes
  // wrong is a row-referenced error.
  const resolveField = (raw: string, row: number): FieldDefinition | null => {
    const t = raw.trim()
    if (!t || t === SELECTION_PLACEHOLDER) return null
    const byDisp = byDisplay.get(t)
    if (byDisp) return byDisp
    const matches = byLabel.get(t) ?? []
    if (matches.length === 1) return matches[0]
    if (matches.length > 1) {
      // The field DOES exist — two of them share this name. "Create it first"
      // would be the wrong advice, so this stays a row-referenced error.
      errors.push(`Row ${row}: ambiguous — ${matches.length} library fields named "${t}".`)
      return null
    }
    if (!missingSeen.has(t)) {
      missingSeen.add(t)
      missingFieldNames.push(t)
    }
    if (cur) cur.unresolved++
    return null
  }

  const lastRow = ws.rowCount
  for (let row = 1; row <= lastRow; row++) {
    const a = cellText(ws.getCell(`A${row}`).value)
    const b = cellText(ws.getCell(`B${row}`).value)
    const key = a.toLowerCase()

    if (key === KEY_FORM_NAME) {
      formName = b
      continue
    }
    if (key === KEY_FORM_TYPE) {
      formTypeText = b
      continue
    }
    // Form-level keys, read only before the first Section block: inside one,
    // a library field named "Label Position" is a field, not the setting.
    if (key === KEY_LABEL_POSITION && !cur) {
      if (b) {
        const p = LABEL_POSITION_BY_TEXT.get(b.toLowerCase())
        if (p) fieldLayout.labelPosition = p
        else errors.push(`Row ${row}: unknown Label Position "${b}" (use Above/Left/Right).`)
      }
      continue
    }
    if (key === KEY_LABEL_WIDTH && !cur) {
      if (b) {
        const w = LABEL_WIDTH_BY_TEXT.get(b.toLowerCase())
        if (w) fieldLayout.labelWidth = w
        else errors.push(`Row ${row}: unknown Label Width "${b}" (use Narrow/Medium/Wide).`)
      }
      continue
    }
    if (key === KEY_SECTION) {
      if (cur) sections.push(cur)
      cur = { startRow: row, title: '', width: 'full', fields: [], unresolved: 0 }
      inFields = false
      continue
    }
    if (!cur) continue // ignore rows before the first Section block
    if (key === KEY_HEADER) {
      cur.title = b
      inFields = false
      continue
    }
    if (key === KEY_SECTION_WIDTH) {
      const w = parseWidth(b)
      if (w === null) errors.push(`Row ${row}: unknown Section Width "${b}" (use Full/Half/Third/Quarter).`)
      else cur.width = w
      inFields = false
      continue
    }
    if (FIELD_NAME_KEYS.has(key)) {
      inFields = true
      continue
    }
    if (inFields && key === KEY_TEXT_BLOCK) {
      // `b` is trimmed at the ends only, so inner line breaks survive. An
      // empty one would render nothing, so it is not placed at all.
      const text = b
      if (text) {
        const block: FieldDefinition = {
          id: '',
          label: 'Text block',
          fieldType: 'description',
          required: false,
          order: 0,
          constantText: text,
        }
        sheetTextBlocks.add(block)
        cur.fields.push(block)
      }
      continue
    }
    // Any other row: a field-slot entry (field names live in column A).
    if (inFields) {
      const field = resolveField(a, row)
      if (field) cur.fields.push(field)
    }
  }
  if (cur) sections.push(cur)

  // Validate structure. Empty example blocks (no title, no fields) are skipped, not errors.
  const built: FormSection[] = []
  // Shared across the whole import so same-label fields in different sections
  // still mint distinct readable ids.
  const usedIds = new Set<string>()
  // library refName -> the form field id this import minted for it, and the
  // refNames placed more than once (so a formula naming one is an error rather
  // than a coin flip between two copies).
  const mintedByRefName = new Map<string, string>()
  const ambiguousRefNames = new Set<string>()
  for (const sec of sections) {
    const hasTitle = sec.title.trim().length > 0
    const hasFields = sec.fields.length > 0
    if (!hasTitle && !hasFields) continue
    if (!hasTitle) {
      errors.push(`Section starting at row ${sec.startRow}: has fields but no Header (title).`)
      continue
    }
    if (!hasFields) {
      // Every field row in this block named a field the library does not have.
      // The missing-fields message already names them; "has no fields" on top of
      // that reads as a second, unrelated problem.
      if (sec.unresolved === 0) {
        errors.push(`Section "${sec.title}" (row ${sec.startRow}): has no fields.`)
      }
      continue
    }
    built.push({
      id: crypto.randomUUID(),
      title: sec.title.trim(),
      width: sec.width,
      fields: sec.fields.map(f => {
        const placed = createFieldFromLibrary(f, { usedIds }) as FormField
        if (sheetTextBlocks.has(f)) placed.libraryFieldId = undefined
        // Pair the library field's reference name with the id this placement
        // just minted, for the rewrite pass below. A refName is unique per
        // tenant, so a second entry can only mean the SAME library field was
        // placed twice — recorded as ambiguous, not silently overwritten.
        if (f.refName) {
          if (mintedByRefName.has(f.refName)) ambiguousRefNames.add(f.refName)
          else mintedByRefName.set(f.refName, placed.id)
        }
        return placed
      }),
    })
  }

  // Second pass: rewrite each calculated field's {tokens} from the library's
  // refName namespace into the ids just minted.
  //
  // This is the whole of z8nrz7dt4c. `createFieldFromLibrary` copies `formula`
  // verbatim while minting a fresh id (`suggestFieldId` appends a random
  // suffix, so the id is unknowable until it exists) — which left every
  // imported operand pointing at a name no field on the form carried, and made
  // an operator re-map each one by hand in the builder. It has to be a second
  // pass because a formula may reference a sibling that is minted later.
  //
  // Tokens we cannot resolve are left exactly as they were: they fall through
  // to the label-similarity guess that has always handled them
  // (`guessOperandMapping`), rather than being dropped.
  const unresolvedRefs: string[] = []
  const unresolvedSeen = new Set<string>()
  for (const sec of built) {
    for (const field of sec.fields) {
      if (field.type !== 'calculated' || !field.formula) continue
      for (const ref of extractFormulaRefs(field.formula)) {
        if (parseCrossFormRef(ref)) continue
        // Ambiguity is checked BEFORE resolution: a refName placed twice is
        // still in `mintedByRefName` (holding whichever copy came first), so
        // testing resolution first would silently pick that one.
        if (ambiguousRefNames.has(ref)) {
          errors.push(
            `Field "${field.label || 'Untitled'}": formula references {${ref}}, but that field is placed ` +
              'more than once — remove the duplicate so the operand names one field.',
          )
          continue
        }
        if (mintedByRefName.has(ref)) continue
        if (!unresolvedSeen.has(ref)) {
          unresolvedSeen.add(ref)
          unresolvedRefs.push(ref)
        }
      }
      field.formula = remapFormulaTokens(field.formula, mintedByRefName)
    }
  }

  if (!formName.trim()) errors.push('Form Name is required (fill the "Form Name" row).')
  const category = parseCategory(formTypeText)
  if (category === null) errors.push(`Unknown Form Type "${formTypeText}".`)
  if (built.length === 0 && errors.length === 0 && missingFieldNames.length === 0) {
    errors.push('No sections with fields found — fill in at least one section.')
  }

  // All-or-nothing, unchanged: unknown field names reject the file just as they
  // always have. Only what the user is told about them has moved.
  if (errors.length > 0 || missingFieldNames.length > 0) {
    return { ok: false, errors, missingFieldNames, unresolvedRefs }
  }

  return {
    ok: true,
    errors: [],
    missingFieldNames: [],
    unresolvedRefs,
    form: {
      meta: { name: formName.trim(), description: '' },
      category: category as FormCategory,
      sections: built,
      ...(fieldLayout.labelPosition || fieldLayout.labelWidth ? { fieldLayout } : {}),
    },
  }
}

// ─── Library Field Import ─────────────────────────────────────────────────────
//
// Separate sheet / separate template from the form-builder import above.
// Columns: Label | Field ID | Field Type | Required | Help Text | Options | Formula | Category
// The Field ID column declares the {placeholder} name each row is known by in
// formulas — e.g. the "Patient Weight" row declares `weight`, and a calculated
// row's formula references `{weight}`. Every formula ref must match a declared
// Field ID in the same file, which makes the operand↔row association explicit.
// Parsed client-side; validated rows are POSTed to POST /field-library/import.

export const SHEET_LIB_IMPORT = 'Library Field Import'
const SHEET_LIB_CATEGORIES = '_Categories'

const LIB_FIELD_TYPES = [
  'text', 'number', 'date', 'textarea', 'checkbox', 'dropdown', 'calculated', 'signature', 'process',
  'attachment',
] as const
type LibFieldType = typeof LIB_FIELD_TYPES[number]

/** Above this row count, import/export still proceeds — just surfaces a heads-up, not a rejection. */
const LARGE_IMPORT_WARNING_THRESHOLD = 2000

// A calculated field's Result Format — how its formula's raw result (number,
// or a Date/Date & time from DATEADD/a date reference) is displayed. Numeric
// formats are meaningless without this column too: a formula returning a Date
// with no Result Format falls back to a raw ISO string ("2026-08-15") instead
// of "Aug 15, 2026" (see formatCalculated) — same gap the ticket flagged for
// dates specifically, so the column supports the full set, not just Date.
const DISPLAY_FORMAT_LABELS: Record<string, string> = {
  number: 'Number',
  decimal2: '2 Decimals',
  decimal3: '3 Decimals',
  date: 'Date',
  datetime: 'Date & time',
}
type LibDisplayFormat = keyof typeof DISPLAY_FORMAT_LABELS
const DISPLAY_FORMAT_VALUES = Object.keys(DISPLAY_FORMAT_LABELS) as LibDisplayFormat[]
const LABEL_TO_DISPLAY_FORMAT = new Map<string, LibDisplayFormat>(
  DISPLAY_FORMAT_VALUES.map(v => [DISPLAY_FORMAT_LABELS[v].toLowerCase(), v]),
)

export interface LibraryFieldImportRow {
  label: string
  /**
   * The sheet's "Field ID" cell — the name this row's formulas are written
   * against. Persisted by the backend as `LibraryField.refName`; omitted when
   * the column is blank, which leaves the field on the label-similarity
   * fallback rather than inventing a name for it.
   */
  refName?: string
  fieldType: LibFieldType
  required: boolean
  /** Offered in insertion pickers when true. Missing/blank column defaults to true. */
  enabled: boolean
  helpText?: string
  options?: string[]
  formula?: string
  categoryName?: string
  /** Per-field attachment config. Omitted when the Attachment column is No/blank. */
  attachment?: AttachmentConfig
  /** Calculated fields only. Blank/omitted defaults to 'number', same as the Form Builder. */
  displayFormat?: LibDisplayFormat
  /**
   * The sheet's "System Name" cell — the stable machine identifier an
   * integration resolves the field by (the dose calculation reads `tnc_per_ml`
   * through it). The backend writes it only onto a field that has none and
   * refuses to change one that has.
   */
  systemName?: string
}

/**
 * A system name is a lower-snake slug, like every curated one. Mirrors
 * `SYSTEM_NAME_RE` in cgt-backend `bulk-import-library-fields.dto.ts`.
 */
const SYSTEM_NAME_PATTERN = /^[a-z][a-z0-9_]*$/

export interface ParseLibraryFieldResult {
  ok: boolean
  rows: LibraryFieldImportRow[]
  /** Human-readable, row-referenced problems. Non-empty iff `ok` is false. */
  errors: string[]
  /** Non-blocking heads-up (e.g. a large file that may take a moment) — `ok` can still be true. */
  warnings: string[]
}

/**
 * Scaffold the "Library Field Import" workbook shared by the blank template and
 * the real-data export: hidden categories sheet, main sheet columns/header/
 * instructions row, and data-validation dropdowns. Callers append their own
 * data rows (starting at row 3) after calling this.
 *
 * Column A: Label, B: Field ID (the {placeholder} name used in formulas),
 * C: Field Type (dropdown), D: Required (Yes/No), E: Help Text,
 * F: Options (semicolon-separated), G: Formula, H: Category (dropdown from existing names),
 * I: Enabled (Yes/No, optional — blank/missing defaults to Yes),
 * J: Attachment (Yes/No), K: Attachment Required (Yes/No), L: Result Format,
 * M: System Name (optional; lower_snake; only fills a field that has none)
 *
 * J/K are appended at the END, not after Category — `Enabled` (I) already sits
 * after Category, so inserting there would shift it and break exactly the
 * pre-existing files the append is meant to keep importable.
 *
 * Category dropdown is backed by a hidden "_Categories" sheet so the reference
 * always exists and there is no 255-char inline-list limit.
 */
function scaffoldLibrarySheet(
  wb: ExcelJS.Workbook,
  categories: FieldCategory[],
): { ws: ExcelJS.Worksheet; categoryFormula: string } {
  // ── Hidden helper sheet: one category name per row (A2 downward) ─────────
  const catSheet = wb.addWorksheet(SHEET_LIB_CATEGORIES)
  catSheet.state = 'veryHidden'
  catSheet.getRow(1).values = ['Category']
  catSheet.getRow(1).font = { bold: true }
  categories.forEach((c, i) => {
    catSheet.getCell(`A${i + 2}`).value = c.name
  })
  catSheet.getColumn(1).width = 32

  // The validation formula references the helper sheet range.
  // When categories is empty, the range A2:A2 still exists (just blank) so
  // Excel accepts the formula without error — the dropdown simply shows nothing.
  const lastCatRow = Math.max(2, categories.length + 1)
  const categoryFormula = `=${SHEET_LIB_CATEGORIES}!$A$2:$A$${lastCatRow}`

  // ── Main import sheet ─────────────────────────────────────────────────────
  const ws = wb.addWorksheet(SHEET_LIB_IMPORT)

  ws.getColumn(1).width = 36
  ws.getColumn(2).width = 16
  ws.getColumn(3).width = 14
  ws.getColumn(4).width = 12
  ws.getColumn(5).width = 36
  ws.getColumn(6).width = 36
  ws.getColumn(7).width = 44
  ws.getColumn(8).width = 24
  ws.getColumn(9).width = 12
  ws.getColumn(10).width = 14
  ws.getColumn(11).width = 20
  ws.getColumn(12).width = 18
  ws.getColumn(13).width = 22

  const hdr = ws.getRow(1)
  hdr.values = ['Label *', 'Field ID', 'Field Type *', 'Required', 'Help Text', 'Options (;-separated)', 'Formula (calculated)', 'Category', 'Enabled', 'Attachment', 'Attachment Required', 'Result Format', 'System Name']
  hdr.font = { bold: true }
  hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EAF6' } }

  // Instructions row
  const info = ws.getRow(2)
  info.values = [
    '← required',
    'Name used in formulas, e.g. weight → {weight}. Kept on import and unique — reuse it to reference this field.',
    `One of: ${LIB_FIELD_TYPES.join(', ')}`,
    'Yes or No',
    'Optional tooltip',
    'For dropdown fields only',
    'e.g. DATEADD({draw_date}, 7, "days") or LOG10({pre} / {post}) — cross-form: {"formhandle".fieldId} or {formhandle.fieldId}',
    'Pick an existing category or type a new name — created on import',
    'Yes or No (blank = Yes)',
    'Yes or No (blank = No)',
    'Yes or No — requires Attachment = Yes',
    'Calculated fields only — blank = Number',
    'Optional, e.g. tnc_per_ml. lower_snake. Set only on a field without one; never changes an existing one.',
  ]
  info.font = { italic: true, color: { argb: 'FF757575' } }

  return { ws, categoryFormula }
}

/**
 * Data validation for every data row, rows 3 through `lastRow` (at least 503,
 * so a fresh blank template always has ~500 pre-validated rows to grow into).
 */
function addLibrarySheetValidations(
  ws: ExcelJS.Worksheet,
  categoryFormula: string,
  lastRow: number,
): void {
  const typeList = `"${LIB_FIELD_TYPES.join(',')}"`
  const displayFormatList = `"${DISPLAY_FORMAT_VALUES.map(v => DISPLAY_FORMAT_LABELS[v]).join(',')}"`
  const end = Math.max(503, lastRow)
  for (let r = 3; r <= end; r++) {
    ws.getCell(`C${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [typeList],
    }
    ws.getCell(`D${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: ['"Yes,No"'],
    }
    // showErrorMessage:false makes the dropdown a suggestion — typing a NEW
    // category name is allowed (the import creates it automatically).
    ws.getCell(`H${r}`).dataValidation = {
      type: 'list', allowBlank: true, showErrorMessage: false, formulae: [categoryFormula],
    }
    ws.getCell(`I${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: ['"Yes,No"'],
    }
    ws.getCell(`J${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: ['"Yes,No"'],
    }
    ws.getCell(`K${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: ['"Yes,No"'],
    }
    ws.getCell(`L${r}`).dataValidation = {
      type: 'list', allowBlank: true, formulae: [displayFormatList],
    }
  }
}

/**
 * Build the blank .xlsx import template for library fields — headers, column
 * guidance, and a handful of worked examples (including a SUM aggregate) to
 * copy from. See {@link buildLibraryFieldExport} for the real-data sibling.
 */
export async function buildLibraryFieldImportTemplate(
  categories: FieldCategory[],
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook()
  const { ws, categoryFormula } = scaffoldLibrarySheet(wb, categories)

  // Example data rows
  const examples: (string | boolean | number)[][] = [
    ['Patient Weight', 'weight', 'number', 'Yes', 'Body weight in kg', '', '', '', 'Yes'],
    ['Patient Height', 'height', 'number', 'Yes', 'Height in metres', '', '', '', 'Yes'],
    ['Diagnosis', 'diagnosis', 'dropdown', 'Yes', '', 'AML;ALL;Myeloma', '', '', 'Yes'],
    ['BMI', 'bmi', 'calculated', 'No', 'Body Mass Index', '', '{weight} / ({height} * {height})', '', '', '', '', '2 Decimals'],
    ['Dose A', 'dose_a', 'number', 'No', 'First dose component (mg)', '', '', '', 'Yes'],
    ['Dose B', 'dose_b', 'number', 'No', 'Second dose component (mg)', '', '', '', 'Yes'],
    ['Total Dose', 'total_dose', 'calculated', 'No', 'Sum of dose components', '', 'SUM({dose_a}, {dose_b})', '', ''],
    ['IDM Draw Date', 'idm_draw_date', 'date', 'Yes', 'Date of the IDM draw', '', '', '', 'Yes'],
    ['IDM Expiration Date', 'idm_expiration_date', 'calculated', 'No', 'Draw date + 7 days', '', 'DATEADD({idm_draw_date}, 7, "days")', '', '', '', '', 'Date'],
    ['IDM Validity Days', 'idm_validity_days', 'calculated', 'No', 'Days between draw and expiration', '', 'DATEDIFF({idm_draw_date}, {idm_expiration_date}, "days")', '', ''],
    ['Pre-selection T-cells', 'pre_selection_tcells', 'number', 'No', 'T-cell count before CD34 selection', '', '', '', 'Yes'],
    ['Post-selection T-cells', 'post_selection_tcells', 'number', 'No', 'T-cell count after CD34 selection', '', '', '', 'Yes'],
    ['T-cell Log Reduction', 'tcell_log_reduction', 'calculated', 'No', 'Log₁₀ depletion achieved by CD34 selection', '', 'LOG10({pre_selection_tcells} / {post_selection_tcells})', '', '', '', '', '2 Decimals'],
    ['Supporting Documents', 'supporting_documents', 'attachment', 'No', 'CoA and shipping paperwork', '', '', '', 'Yes'],
  ]
  for (const ex of examples) {
    ws.addRow(ex)
  }

  addLibrarySheetValidations(ws, categoryFormula, 2 + examples.length)
  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>
}

/** A form another file's formula may reference by handle (`{handle.fieldId}`). */
export interface KnownFormForImport {
  handle: string
  name: string
  fieldIds: Set<string>
}

// ─── Export (existing fields → .xlsx) ───────────────────────────────────────

/**
 * Deterministic, collision-free slug for a field's Excel "Field ID" cell.
 * `LibraryField` has no persisted placeholder-name column (the import sheet's
 * Field ID is a file-scoped authoring convenience only, never stored server
 * side — see `formula-engine`/field-library grounding on ticket 86eyf0nhd),
 * so export must invent one. Derived from the label so it reads naturally and
 * so `rewriteFormulaForExport`'s label-matching can find it again.
 */
function slugifyLabel(label: string): string {
  const base = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return base || 'field'
}

function assignExportFieldIds(fields: FieldDefinition[]): Map<string, string> {
  const used = new Set<string>()
  const idBySlug = new Map<string, string>() // fieldId -> slug
  // A persisted refName wins: it is the name the author gave this field and the
  // name its formulas are already written against, so writing it back makes the
  // round-trip exact instead of relying on `slugifyLabel` landing on the same
  // string both ways. Claimed first, before any slug can take the name.
  for (const f of fields) {
    if (!f.refName || used.has(f.refName)) continue
    used.add(f.refName)
    idBySlug.set(f.id, f.refName)
  }
  for (const f of fields) {
    if (idBySlug.has(f.id)) continue
    const base = slugifyLabel(f.label)
    let candidate = base
    let n = 2
    while (used.has(candidate)) candidate = `${base}_${n++}`
    used.add(candidate)
    idBySlug.set(f.id, candidate)
  }
  return idBySlug
}

function normalizeForMatch(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

/**
 * Best-effort guess at which library field a formula ref token was originally
 * meant to name, by fuzzy-matching against field labels — the same heuristic
 * `guessOperandMapping` (FormEditorPage.tsx) uses to re-map library-field
 * placeholders onto real form fields. Refs typed via the UI are free text with
 * no persisted link to a specific field, so this is necessarily a guess: it
 * resolves refs that were named after their field (the common case) and
 * leaves anything else untouched rather than silently dropping it.
 */
function guessFieldForRef(ref: string, fields: FieldDefinition[]): FieldDefinition | undefined {
  const r = normalizeForMatch(ref)
  if (!r) return undefined
  // Exact match always wins, regardless of array order — otherwise an
  // earlier substring collision (e.g. "Total Weight" for ref "weight") could
  // beat a later exact match ("Weight"), silently rewriting the wrong operand.
  const exact = fields.find(f => normalizeForMatch(f.label || '') === r)
  if (exact) return exact
  return fields.find(f => {
    const l = normalizeForMatch(f.label || '')
    if (!l) return false
    if (r.length >= 3 && l.includes(r)) return true
    return l.length >= 3 && r.includes(l)
  })
}

/** Rewrite a calculated field's `{ref}` tokens to the newly-assigned export Field IDs. */
function rewriteFormulaForExport(
  formula: string,
  fields: FieldDefinition[],
  idBySlug: Map<string, string>,
): string {
  return formula.replace(/\{([^}]+)\}/g, (whole, ref: string) => {
    // An exact refName match always wins over the label guess: once a field
    // carries the name its formulas were written against, guessing is not only
    // unnecessary but wrong — "Total Weight" could out-match "Weight" for the
    // ref `weight` and silently rewrite the operand to a different field.
    const hit = fields.find(f => f.refName === ref) ?? guessFieldForRef(ref, fields)
    if (!hit) return whole
    const slug = idBySlug.get(hit.id)
    return slug ? `{${slug}}` : whole
  })
}

export interface LibraryFieldExportResult {
  buf: ArrayBuffer
  /**
   * Fields written to no row because their type isn't one of `LIB_FIELD_TYPES`
   * — the import sheet has no column value that represents them, and including
   * one unrecognized Field Type would reject the *entire* re-import (parsing
   * is all-or-nothing). In practice this is `section`/`table` only: they carry
   * structured config (a layout container, a nested grid) that doesn't fit a
   * flat row, and neither is even selectable when creating a real library
   * field today, so this is defensive coverage rather than an expected case.
   * Report whatever is skipped so the caller can tell the admin what was left
   * out and why, rather than silently shrinking the export.
   */
  skipped: { label: string; fieldType: string }[]
}

/**
 * Build a .xlsx snapshot of every current library field, in the same sheet/
 * column shape as {@link buildLibraryFieldImportTemplate} so it re-imports as
 * an upsert-by-label with no unintended diffs. Field IDs and calculated-field
 * formula refs are regenerated at export time (see {@link slugifyLabel},
 * {@link rewriteFormulaForExport}) since neither is persisted server side.
 * No row-count limit — a library of any size exports and re-imports in full
 * (`parseLibraryFieldWorkbook` only warns, never rejects, above a large-file
 * threshold; see `LARGE_IMPORT_WARNING_THRESHOLD`).
 */
export async function buildLibraryFieldExport(
  fields: FieldDefinition[],
  categories: FieldCategory[],
): Promise<LibraryFieldExportResult> {
  const wb = new ExcelJS.Workbook()
  const { ws, categoryFormula } = scaffoldLibrarySheet(wb, categories)

  const exportable = fields.filter(f => (LIB_FIELD_TYPES as readonly string[]).includes(f.fieldType))
  const skipped = fields
    .filter(f => !(LIB_FIELD_TYPES as readonly string[]).includes(f.fieldType))
    .map(f => ({ label: f.label, fieldType: f.fieldType }))

  const categoryNameById = new Map(categories.map(c => [c.id, c.name]))
  const idBySlug = assignExportFieldIds(exportable)

  for (const f of exportable) {
    const categoryName = f.categoryIds?.length ? categoryNameById.get(f.categoryIds[0]) : undefined
    const formula =
      f.fieldType === 'calculated' && f.formula
        ? rewriteFormulaForExport(f.formula, exportable, idBySlug)
        : f.formula
    ws.addRow([
      f.label,
      idBySlug.get(f.id) ?? '',
      f.fieldType,
      f.required ? 'Yes' : 'No',
      f.helpText ?? '',
      f.options?.join(';') ?? '',
      formula ?? '',
      categoryName ?? '',
      f.enabled === false ? 'No' : 'Yes',
      f.attachment?.enabled ? 'Yes' : 'No',
      f.attachment?.enabled && f.attachment.required ? 'Yes' : 'No',
      f.fieldType === 'calculated' && f.displayFormat ? DISPLAY_FORMAT_LABELS[f.displayFormat] ?? '' : '',
      // Only a CURATED name — never the provisional one derived from the label,
      // which would turn a display stand-in into a stored identifier on re-import.
      f.systemName ?? '',
    ])
  }

  addLibrarySheetValidations(ws, categoryFormula, 2 + exportable.length)
  const buf = (await wb.xlsx.writeBuffer()) as ArrayBuffer
  return { buf, skipped }
}

/**
 * Parse a filled Library Field Import workbook.
 * All-or-nothing: any invalid row makes the whole result fail with row-referenced errors.
 * Validates field types, intra-file duplicate labels, and calculated-field formulas.
 * Category-name resolution against the DB happens server-side.
 *
 * Formula refs resolve two ways: `{fieldId}` against the file's own Field ID
 * column (as before), and cross-form `{formhandle.fieldId}` against
 * `knownForms`. When `knownForms` is omitted (forms list unavailable),
 * cross-form refs pass through — the backend import re-validates them.
 */
export async function parseLibraryFieldWorkbook(
  data: ArrayBuffer | Uint8Array,
  knownForms?: KnownFormForImport[],
): Promise<ParseLibraryFieldResult> {
  const wb = new ExcelJS.Workbook()
  try {
    await wb.xlsx.load(data as ArrayBuffer)
  } catch {
    return { ok: false, rows: [], errors: ['Could not read the file as an Excel workbook (.xlsx).'], warnings: [] }
  }

  const ws = wb.getWorksheet(SHEET_LIB_IMPORT)
  if (!ws) {
    return {
      ok: false,
      rows: [],
      errors: [`Missing a "${SHEET_LIB_IMPORT}" sheet — use the downloaded template.`],
      warnings: [],
    }
  }

  // Old template layout (pre-Field ID column) had Field Type in column B —
  // fail with a clear pointer instead of a confusing "unknown Field Type" wall.
  const b1 = cellText(ws.getCell('B1').value).trim().toLowerCase()
  if (b1.startsWith('field type')) {
    return {
      ok: false,
      rows: [],
      errors: [
        'This file uses an outdated template (no "Field ID" column). Download the latest template and re-fill it.',
      ],
      warnings: [],
    }
  }

  const errors: string[] = []
  const rows: LibraryFieldImportRow[] = []
  const seenLabels = new Map<string, number>() // label.lower -> row number
  const seenFieldIds = new Map<string, number>() // fieldId -> row number
  // Calculated rows deferred for formula-ref validation once all Field IDs are known.
  const calcRows: { rowNum: number; formula: string }[] = []

  // Start at row 3 — row 1 is headers, row 2 is instructions.
  for (let r = 3; r <= ws.rowCount; r++) {
    const label = cellText(ws.getCell(`A${r}`).value).trim()
    const fieldId = cellText(ws.getCell(`B${r}`).value).trim()
    const fieldTypeRaw = cellText(ws.getCell(`C${r}`).value).trim().toLowerCase()
    const requiredRaw = cellText(ws.getCell(`D${r}`).value).trim().toLowerCase()
    const helpText = cellText(ws.getCell(`E${r}`).value).trim()
    const optionsRaw = cellText(ws.getCell(`F${r}`).value).trim()
    // Normalize the Unicode operators the Form Builder UI displays (× ÷ − ≠ ≤
    // ≥) to their canonical ASCII tokens BEFORE anything else touches this
    // formula — the tokenizer silently drops unrecognized characters rather
    // than erroring, which used to turn "×"/"÷" into a corrupt implicit "+".
    // Then accept the quoted cross-form spelling ({"formhandle".fieldId}) —
    // stored formulas always use the canonical {formhandle.fieldId} form.
    const { formula: formulaOpsNormalized, unsupported: unsupportedFormulaChars } =
      normalizeFormulaOperatorSymbols(cellText(ws.getCell(`G${r}`).value).trim())
    const formula = normalizeCrossFormRefSyntax(formulaOpsNormalized)
    const categoryName = cellText(ws.getCell(`H${r}`).value).trim()
    const enabledRaw = cellText(ws.getCell(`I${r}`).value).trim().toLowerCase()
    const attachmentRaw = cellText(ws.getCell(`J${r}`).value).trim().toLowerCase()
    const attachmentRequiredRaw = cellText(ws.getCell(`K${r}`).value).trim().toLowerCase()
    const displayFormatRaw = cellText(ws.getCell(`L${r}`).value).trim()
    const systemName = cellText(ws.getCell(`M${r}`).value).trim()

    // Skip empty rows.
    if (!label && !fieldTypeRaw) continue

    if (!label) { errors.push(`Row ${r}: Label is required.`); continue }

    // Duplicate labels within file.
    const labelKey = label.toLowerCase()
    const prevRow = seenLabels.get(labelKey)
    if (prevRow !== undefined) {
      errors.push(`Row ${r}: label "${label}" duplicates row ${prevRow}.`)
      continue
    }
    seenLabels.set(labelKey, r)

    // Field ID: optional, but when present must be formula-token safe and unique.
    if (fieldId) {
      if (!/^[A-Za-z0-9_-]+$/.test(fieldId)) {
        errors.push(`Row ${r}: Field ID "${fieldId}" may only contain letters, digits, _ and - (no spaces or braces).`)
        continue
      }
      const prevIdRow = seenFieldIds.get(fieldId)
      if (prevIdRow !== undefined) {
        errors.push(`Row ${r}: Field ID "${fieldId}" duplicates row ${prevIdRow}.`)
        continue
      }
      seenFieldIds.set(fieldId, r)
    }

    if (systemName && !SYSTEM_NAME_PATTERN.test(systemName)) {
      errors.push(
        `Row ${r}: System Name "${systemName}" must be lower-case letters, digits and _, starting with a letter (e.g. tnc_per_ml).`,
      )
      continue
    }

    if (!fieldTypeRaw) { errors.push(`Row ${r}: Field Type is required.`); continue }
    if (!(LIB_FIELD_TYPES as readonly string[]).includes(fieldTypeRaw)) {
      errors.push(`Row ${r}: unknown Field Type "${fieldTypeRaw}". Must be one of: ${LIB_FIELD_TYPES.join(', ')}.`)
      continue
    }
    const fieldType = fieldTypeRaw as LibFieldType

    if (fieldType === 'calculated' && !formula) {
      errors.push(`Row ${r}: calculated field requires a Formula.`)
      continue
    }
    // Reject rather than silently drop — an unrecognized character must never
    // reach the tokenizer, where it would vanish and get misread as an
    // implicit "+" between its neighboring operands.
    if (fieldType === 'calculated' && unsupportedFormulaChars.length > 0) {
      errors.push(
        `Row ${r}, column Formula: unsupported character(s) ${unsupportedFormulaChars.map(c => `"${c}"`).join(', ')}. ` +
          'Supported operators: × ÷ − ≠ ≤ ≥ (or their ASCII equivalents * / - != <= >=).',
      )
      continue
    }
    if (fieldType === 'calculated' && formula) calcRows.push({ rowNum: r, formula })

    // Surfaced as a row error, same policy as Attachment Required above — a
    // Result Format on a non-calculated row is either a leftover from copying
    // another row or a misunderstanding, and silently ignoring it would just
    // let the import "succeed" while doing something the author didn't intend.
    let displayFormat: LibDisplayFormat | undefined
    if (displayFormatRaw) {
      const matched = LABEL_TO_DISPLAY_FORMAT.get(displayFormatRaw.toLowerCase())
      if (!matched) {
        errors.push(
          `Row ${r}: unknown Result Format "${displayFormatRaw}" (use ${DISPLAY_FORMAT_VALUES.map(v => DISPLAY_FORMAT_LABELS[v]).join('/')}).`,
        )
        continue
      }
      if (fieldType !== 'calculated') {
        errors.push(`Row ${r}: Result Format is set but Field Type is "${fieldType}", not calculated — clear it or set Field Type to calculated.`)
        continue
      }
      displayFormat = matched
    }

    const required = requiredRaw === 'yes' || requiredRaw === 'true' || requiredRaw === '1'
    // Attachment defaults to No when blank (unlike Enabled), so pre-existing
    // files without columns J/K import as "no attachment".
    const attachmentOn = isYes(attachmentRaw)
    const attachmentRequired = isYes(attachmentRequiredRaw)
    // Surfaced as a row error rather than silently coerced — an author who
    // marked the attachment mandatory but left the parent off has made a
    // mistake we should not guess the resolution of.
    if (attachmentRequired && !attachmentOn) {
      errors.push(
        `Row ${r}: Attachment Required is Yes but Attachment is No — set Attachment to Yes, or clear Attachment Required.`,
      )
      continue
    }
    if (attachmentOn && !supportsAttachment(fieldType)) {
      errors.push(
        `Row ${r}: field type "${fieldType}" cannot carry an attachment. Set Attachment to No.`,
      )
      continue
    }
    // Blank/missing column is backward-compatible with pre-Enabled templates and
    // defaults to enabled — unlike Required, which defaults to false when blank.
    const enabled = enabledRaw === '' || enabledRaw === 'yes' || enabledRaw === 'true' || enabledRaw === '1'
    const options = optionsRaw
      ? optionsRaw.split(';').map(o => o.trim()).filter(Boolean)
      : undefined

    rows.push({
      label,
      // Carried through rather than dropped after validation (z8nrz7dt4b): the
      // backend persists it as `LibraryField.refName`, which is what lets a
      // formula's {token} resolve to a real row later instead of being guessed
      // at from labels when the field is placed on a form.
      refName: fieldId || undefined,
      fieldType,
      required,
      enabled,
      helpText: helpText || undefined,
      options: fieldType === 'dropdown' ? (options ?? []) : undefined,
      formula: fieldType === 'calculated' ? formula || undefined : undefined,
      categoryName: categoryName || undefined,
      attachment: attachmentOn ? { enabled: true, required: attachmentRequired } : undefined,
      displayFormat,
      systemName: systemName || undefined,
    })
  }

  // Every same-file {ref} in a calculated formula must be declared as some
  // row's Field ID — this is what associates the operand with the row it reads
  // from. Cross-form refs ({formhandle.fieldId}) resolve against knownForms.
  const formsByHandle = knownForms
    ? new Map(knownForms.map(f => [f.handle, f]))
    : undefined
  for (const { rowNum, formula } of calcRows) {
    for (const ref of extractFormulaRefs(formula)) {
      if (seenFieldIds.has(ref)) continue
      const cross = parseCrossFormRef(ref)
      if (cross) {
        if (!formsByHandle) continue // forms list unavailable — server re-validates
        const form = formsByHandle.get(cross.handle)
        if (!form) {
          errors.push(
            `Row ${rowNum}: formula references {${ref}} but no form has the handle "${cross.handle}". Check the Handle column on the Forms page.`,
          )
        } else if (!form.fieldIds.has(cross.fieldId)) {
          errors.push(
            `Row ${rowNum}: formula references {${ref}} but form "${form.name}" (${cross.handle}) has no field with ID "${cross.fieldId}".`,
          )
        }
        continue
      }
      errors.push(
        `Row ${rowNum}: formula references {${ref}} but no row declares Field ID "${ref}". Add "${ref}" in the Field ID column of the row it refers to.`,
      )
    }
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No data rows found — fill in at least one field row (starting at row 3).')
  }

  const warnings: string[] = []
  if (rows.length > LARGE_IMPORT_WARNING_THRESHOLD) {
    warnings.push(`This is a large import (${rows.length} rows) — it may take a moment to process.`)
  }

  if (errors.length > 0) return { ok: false, rows: [], errors, warnings: [] }
  return { ok: true, rows, errors: [], warnings }
}
